import React, { useEffect, useState, useCallback } from 'react';
import { tasksApi, approvalsApi } from '../api/client';
import { useToast } from '../components/Toast';

// ─── Status badge colors ────────────────────────────────────────────────────────
const TASK_STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  not_started: { bg: '#f3f4f6', color: '#374151' },
  in_progress: { bg: '#dbeafe', color: '#1d4ed8' },
  blocked: { bg: '#fee2e2', color: '#991b1b' },
  submitted_for_review: { bg: '#fef3c7', color: '#92400e' },
  returned: { bg: '#ffedd5', color: '#c2410c' },
  completed: { bg: '#d1fae5', color: '#065f46' },
  cancelled: { bg: '#f3f4f6', color: '#6b7280' },
  waived: { bg: '#e0e7ff', color: '#4338ca' },
};

const APPROVAL_STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  pending: { bg: '#fef3c7', color: '#92400e' },
  approved: { bg: '#d1fae5', color: '#065f46' },
  rejected: { bg: '#fee2e2', color: '#991b1b' },
  returned_for_revision: { bg: '#ffedd5', color: '#c2410c' },
};

const TASK_STATUSES = ['not_started', 'in_progress', 'blocked', 'submitted_for_review', 'returned', 'completed', 'cancelled', 'waived'];
const PRIORITY_OPTIONS = ['low', 'normal', 'high', 'critical'];

type Tab = 'tasks' | 'approvals';
type TaskView = 'list' | 'detail' | 'create';
type ApprovalView = 'list' | 'detail' | 'create';

// ─── Empty form defaults ────────────────────────────────────────────────────────
const EMPTY_TASK: Record<string, string> = { title: '', description: '', case_id: '', stage: '', assignee: '', due_date: '', priority: 'normal' };
const EMPTY_APPROVAL: Record<string, string> = { title: '', description: '', case_id: '', object_type: '', approver: '', required_role: '' };

export default function TasksApprovals() {
  const { toast } = useToast();
  const [tab, setTab] = useState<Tab>('tasks');

  // ─── Tasks state ──────────────────────────────────────────────────────────────
  const [tasks, setTasks] = useState<any[]>([]);
  const [taskView, setTaskView] = useState<TaskView>('list');
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [taskForm, setTaskForm] = useState<Record<string, string>>({ ...EMPTY_TASK });
  const [taskFilters, setTaskFilters] = useState<Record<string, string>>({ case_id: '', status: '', assignee: '' });
  const [taskLoading, setTaskLoading] = useState(true);
  const [taskSaving, setTaskSaving] = useState(false);
  const [transitionReason, setTransitionReason] = useState('');
  const [showTransition, setShowTransition] = useState('');

  // ─── Approvals state ──────────────────────────────────────────────────────────
  const [approvals, setApprovals] = useState<any[]>([]);
  const [approvalView, setApprovalView] = useState<ApprovalView>('list');
  const [selectedApproval, setSelectedApproval] = useState<any>(null);
  const [approvalForm, setApprovalForm] = useState<Record<string, string>>({ ...EMPTY_APPROVAL });
  const [approvalFilters, setApprovalFilters] = useState<Record<string, string>>({ status: '', case_id: '' });
  const [approvalLoading, setApprovalLoading] = useState(true);
  const [approvalSaving, setApprovalSaving] = useState(false);
  const [decisionRationale, setDecisionRationale] = useState('');
  const [showDecision, setShowDecision] = useState('');

  // ─── Load functions ───────────────────────────────────────────────────────────
  const loadTasks = useCallback(async () => {
    setTaskLoading(true);
    try {
      const params: Record<string, string> = {};
      Object.entries(taskFilters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await tasksApi.list(Object.keys(params).length > 0 ? params : undefined);
      setTasks(res.items || []);
    } catch (e) { console.error(e); }
    setTaskLoading(false);
  }, [taskFilters]);

  const loadApprovals = useCallback(async () => {
    setApprovalLoading(true);
    try {
      const params: Record<string, string> = {};
      Object.entries(approvalFilters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await approvalsApi.list(Object.keys(params).length > 0 ? params : undefined);
      setApprovals(res.items || []);
    } catch (e) { console.error(e); }
    setApprovalLoading(false);
  }, [approvalFilters]);

  useEffect(() => { loadTasks(); }, [loadTasks]);
  useEffect(() => { loadApprovals(); }, [loadApprovals]);

  // ─── Badge helper ─────────────────────────────────────────────────────────────
  const renderBadge = (status: string, colors: Record<string, { bg: string; color: string }>) => {
    const s = colors[status] || { bg: '#f3f4f6', color: '#374151' };
    return (
      <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color }}>
        {status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
      </span>
    );
  };

  // ─── Task handlers ────────────────────────────────────────────────────────────
  const handleTaskCreate = async () => {
    if (!taskForm.title.trim()) return toast('Title is required', 'warning');
    setTaskSaving(true);
    try {
      await tasksApi.create(taskForm);
      await loadTasks();
      setTaskView('list');
      setTaskForm({ ...EMPTY_TASK });
    } catch (e: any) { toast(e.message || 'Create failed', 'error'); }
    setTaskSaving(false);
  };

  const handleTaskClick = async (task: any) => {
    try {
      const detail = await tasksApi.get(task.id);
      setSelectedTask(detail);
      setTaskView('detail');
    } catch (e) { console.error(e); }
  };

  const handleTaskTransition = async (newStatus: string) => {
    setShowTransition(newStatus);
  };

  const submitTaskTransition = async () => {
    if (!selectedTask?.id || !showTransition) return;
    setTaskSaving(true);
    try {
      await tasksApi.transition(selectedTask.id, showTransition, transitionReason);
      setShowTransition('');
      setTransitionReason('');
      await loadTasks();
      setTaskView('list');
    } catch (e: any) { toast(e.message || 'Transition failed', 'error'); }
    setTaskSaving(false);
  };

  // ─── Approval handlers ────────────────────────────────────────────────────────
  const handleApprovalCreate = async () => {
    if (!approvalForm.title.trim()) return toast('Title is required', 'warning');
    setApprovalSaving(true);
    try {
      await approvalsApi.request(approvalForm);
      await loadApprovals();
      setApprovalView('list');
      setApprovalForm({ ...EMPTY_APPROVAL });
    } catch (e: any) { toast(e.message || 'Request failed', 'error'); }
    setApprovalSaving(false);
  };

  const handleApprovalClick = async (approval: any) => {
    try {
      const detail = await approvalsApi.get(approval.id);
      setSelectedApproval(detail);
      setApprovalView('detail');
    } catch (e) { console.error(e); }
  };

  const handleDecision = (decision: string) => {
    setShowDecision(decision);
  };

  const submitDecision = async () => {
    if (!selectedApproval?.id || !showDecision) return;
    setApprovalSaving(true);
    try {
      await approvalsApi.decide(selectedApproval.id, showDecision, decisionRationale);
      setShowDecision('');
      setDecisionRationale('');
      await loadApprovals();
      setApprovalView('list');
    } catch (e: any) { toast(e.message || 'Decision failed', 'error'); }
    setApprovalSaving(false);
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════════

  return (
    <div style={{ maxWidth: 1200 }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, margin: 0 }}>Tasks & Approvals</h2>
        <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Manage workflow tasks and approval requests</p>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid #e5e7eb' }}>
        {(['tasks', 'approvals'] as Tab[]).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '10px 20px',
              fontSize: 13,
              fontWeight: 600,
              border: 'none',
              borderBottom: tab === t ? '2px solid #4f46e5' : '2px solid transparent',
              background: 'none',
              color: tab === t ? '#4f46e5' : '#6b7280',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {t === 'tasks' ? 'Tasks' : 'Approvals'}
          </button>
        ))}
      </div>

      {/* ═══════════════════ TASKS TAB ═══════════════════ */}
      {tab === 'tasks' && (
        <>
          {/* Tasks List View */}
          {taskView === 'list' && (
            <>
              {/* Filter bar */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
                <input
                  placeholder="Filter by Case ID"
                  value={taskFilters.case_id}
                  onChange={e => setTaskFilters(p => ({ ...p, case_id: e.target.value }))}
                  style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, width: 160 }}
                />
                <select
                  value={taskFilters.status}
                  onChange={e => setTaskFilters(p => ({ ...p, status: e.target.value }))}
                  style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
                >
                  <option value="">All Statuses</option>
                  {TASK_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
                </select>
                <input
                  placeholder="Filter by Assignee"
                  value={taskFilters.assignee}
                  onChange={e => setTaskFilters(p => ({ ...p, assignee: e.target.value }))}
                  style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, width: 160 }}
                />
                <div style={{ marginLeft: 'auto' }}>
                  <button
                    onClick={() => { setTaskForm({ ...EMPTY_TASK }); setTaskView('create'); }}
                    style={{ padding: '9px 18px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                  >
                    + New Task
                  </button>
                </div>
              </div>

              <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                {taskLoading ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>Loading tasks...</div>
                ) : tasks.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>No tasks found.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 11, textTransform: 'uppercase' }}>Title</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 11, textTransform: 'uppercase' }}>Case ID</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 11, textTransform: 'uppercase' }}>Stage</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 11, textTransform: 'uppercase' }}>Assignee</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 11, textTransform: 'uppercase' }}>Due Date</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 11, textTransform: 'uppercase' }}>Priority</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 11, textTransform: 'uppercase' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map((task: any) => (
                        <tr
                          key={task.id}
                          onClick={() => handleTaskClick(task)}
                          style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer', transition: 'background 0.1s' }}
                          onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'white')}
                        >
                          <td style={{ padding: '10px 14px', fontWeight: 500 }}>{task.title || '—'}</td>
                          <td style={{ padding: '10px 14px', color: '#6b7280', fontFamily: 'monospace', fontSize: 12 }}>{task.case_id || '—'}</td>
                          <td style={{ padding: '10px 14px' }}>{task.stage || '—'}</td>
                          <td style={{ padding: '10px 14px' }}>{task.assignee || '—'}</td>
                          <td style={{ padding: '10px 14px', color: '#6b7280' }}>{task.due_date || '—'}</td>
                          <td style={{ padding: '10px 14px', textTransform: 'capitalize' }}>{task.priority || 'normal'}</td>
                          <td style={{ padding: '10px 14px' }}>{renderBadge(task.status || 'not_started', TASK_STATUS_COLORS)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {/* Tasks Create View */}
          {taskView === 'create' && (
            <div style={{ maxWidth: 600 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <button onClick={() => setTaskView('list')} style={{ background: 'none', border: 'none', fontSize: 13, color: '#4f46e5', cursor: 'pointer', fontWeight: 600 }}>← Back</button>
                <h3 style={{ fontSize: 16, margin: 0 }}>New Task</h3>
              </div>
              <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 24 }}>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Title <span style={{ color: '#dc2626' }}>*</span></label>
                  <input value={taskForm.title} onChange={e => setTaskForm(p => ({ ...p, title: e.target.value }))} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Description</label>
                  <textarea value={taskForm.description} onChange={e => setTaskForm(p => ({ ...p, description: e.target.value }))} rows={3} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Case ID</label>
                    <input value={taskForm.case_id} onChange={e => setTaskForm(p => ({ ...p, case_id: e.target.value }))} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Stage</label>
                    <input value={taskForm.stage} onChange={e => setTaskForm(p => ({ ...p, stage: e.target.value }))} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Assignee</label>
                    <input value={taskForm.assignee} onChange={e => setTaskForm(p => ({ ...p, assignee: e.target.value }))} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Due Date</label>
                    <input type="date" value={taskForm.due_date} onChange={e => setTaskForm(p => ({ ...p, due_date: e.target.value }))} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
                  </div>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Priority</label>
                  <select value={taskForm.priority} onChange={e => setTaskForm(p => ({ ...p, priority: e.target.value }))} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
                    {PRIORITY_OPTIONS.map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
                  <button onClick={handleTaskCreate} disabled={taskSaving} style={{ padding: '10px 20px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    {taskSaving ? 'Creating...' : 'Create Task'}
                  </button>
                  <button onClick={() => setTaskView('list')} style={{ padding: '10px 20px', background: 'none', color: '#6b7280', border: 'none', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Tasks Detail View */}
          {taskView === 'detail' && selectedTask && (
            <div style={{ maxWidth: 700 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <button onClick={() => setTaskView('list')} style={{ background: 'none', border: 'none', fontSize: 13, color: '#4f46e5', cursor: 'pointer', fontWeight: 600 }}>← Back</button>
                <h3 style={{ fontSize: 16, margin: 0 }}>{selectedTask.title}</h3>
                <div style={{ marginLeft: 'auto' }}>{renderBadge(selectedTask.status || 'not_started', TASK_STATUS_COLORS)}</div>
              </div>

              <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 24 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', marginBottom: 20 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 2 }}>Case ID</div>
                    <div style={{ fontSize: 13, color: '#1e293b', fontFamily: 'monospace' }}>{selectedTask.case_id || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 2 }}>Stage</div>
                    <div style={{ fontSize: 13, color: '#1e293b' }}>{selectedTask.stage || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 2 }}>Assignee</div>
                    <div style={{ fontSize: 13, color: '#1e293b' }}>{selectedTask.assignee || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 2 }}>Due Date</div>
                    <div style={{ fontSize: 13, color: '#1e293b' }}>{selectedTask.due_date || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 2 }}>Priority</div>
                    <div style={{ fontSize: 13, color: '#1e293b', textTransform: 'capitalize' }}>{selectedTask.priority || 'normal'}</div>
                  </div>
                </div>

                {selectedTask.description && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>Description</div>
                    <div style={{ fontSize: 13, color: '#1e293b', whiteSpace: 'pre-wrap' }}>{selectedTask.description}</div>
                  </div>
                )}

                {/* Status Transition Actions */}
                <div style={{ paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 10 }}>Actions</div>
                  {showTransition ? (
                    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                        Reason for "{showTransition.replace(/_/g, ' ')}" (optional):
                      </label>
                      <textarea
                        value={transitionReason}
                        onChange={e => setTransitionReason(e.target.value)}
                        rows={2}
                        placeholder="Optional reason..."
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, marginBottom: 10, fontFamily: 'inherit' }}
                      />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={submitTaskTransition} disabled={taskSaving} style={{ padding: '8px 16px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          {taskSaving ? 'Updating...' : 'Confirm'}
                        </button>
                        <button onClick={() => { setShowTransition(''); setTransitionReason(''); }} style={{ padding: '8px 16px', background: 'none', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {selectedTask.status === 'not_started' && (
                        <button onClick={() => handleTaskTransition('in_progress')} style={{ padding: '8px 14px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Start</button>
                      )}
                      {(selectedTask.status === 'in_progress' || selectedTask.status === 'not_started') && (
                        <button onClick={() => handleTaskTransition('blocked')} style={{ padding: '8px 14px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Block</button>
                      )}
                      {selectedTask.status === 'in_progress' && (
                        <button onClick={() => handleTaskTransition('submitted_for_review')} style={{ padding: '8px 14px', background: '#d97706', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Submit for Review</button>
                      )}
                      {(selectedTask.status === 'in_progress' || selectedTask.status === 'submitted_for_review') && (
                        <button onClick={() => handleTaskTransition('completed')} style={{ padding: '8px 14px', background: '#059669', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Complete</button>
                      )}
                      {selectedTask.status !== 'completed' && selectedTask.status !== 'cancelled' && (
                        <button onClick={() => handleTaskTransition('cancelled')} style={{ padding: '8px 14px', background: '#6b7280', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══════════════════ APPROVALS TAB ═══════════════════ */}
      {tab === 'approvals' && (
        <>
          {/* Approvals List View */}
          {approvalView === 'list' && (
            <>
              {/* Filter bar */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
                <select
                  value={approvalFilters.status}
                  onChange={e => setApprovalFilters(p => ({ ...p, status: e.target.value }))}
                  style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
                <input
                  placeholder="Filter by Case ID"
                  value={approvalFilters.case_id}
                  onChange={e => setApprovalFilters(p => ({ ...p, case_id: e.target.value }))}
                  style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, width: 160 }}
                />
                <div style={{ marginLeft: 'auto' }}>
                  <button
                    onClick={() => { setApprovalForm({ ...EMPTY_APPROVAL }); setApprovalView('create'); }}
                    style={{ padding: '9px 18px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                  >
                    + Request Approval
                  </button>
                </div>
              </div>

              <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                {approvalLoading ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>Loading approvals...</div>
                ) : approvals.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>No approvals found.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 11, textTransform: 'uppercase' }}>Title</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 11, textTransform: 'uppercase' }}>Type</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 11, textTransform: 'uppercase' }}>Requested By</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 11, textTransform: 'uppercase' }}>Approver</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 11, textTransform: 'uppercase' }}>Status</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 11, textTransform: 'uppercase' }}>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {approvals.map((approval: any) => (
                        <tr
                          key={approval.id}
                          onClick={() => handleApprovalClick(approval)}
                          style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer', transition: 'background 0.1s' }}
                          onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'white')}
                        >
                          <td style={{ padding: '10px 14px', fontWeight: 500 }}>{approval.title || '—'}</td>
                          <td style={{ padding: '10px 14px' }}>{approval.object_type || '—'}</td>
                          <td style={{ padding: '10px 14px' }}>{approval.requested_by || '—'}</td>
                          <td style={{ padding: '10px 14px' }}>{approval.approver || '—'}</td>
                          <td style={{ padding: '10px 14px' }}>{renderBadge(approval.status || 'pending', APPROVAL_STATUS_COLORS)}</td>
                          <td style={{ padding: '10px 14px', color: '#6b7280' }}>{approval.created_at ? new Date(approval.created_at).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {/* Approvals Create View */}
          {approvalView === 'create' && (
            <div style={{ maxWidth: 600 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <button onClick={() => setApprovalView('list')} style={{ background: 'none', border: 'none', fontSize: 13, color: '#4f46e5', cursor: 'pointer', fontWeight: 600 }}>← Back</button>
                <h3 style={{ fontSize: 16, margin: 0 }}>Request Approval</h3>
              </div>
              <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 24 }}>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Title <span style={{ color: '#dc2626' }}>*</span></label>
                  <input value={approvalForm.title} onChange={e => setApprovalForm(p => ({ ...p, title: e.target.value }))} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Description</label>
                  <textarea value={approvalForm.description} onChange={e => setApprovalForm(p => ({ ...p, description: e.target.value }))} rows={3} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Case ID</label>
                    <input value={approvalForm.case_id} onChange={e => setApprovalForm(p => ({ ...p, case_id: e.target.value }))} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Object Type</label>
                    <input value={approvalForm.object_type} onChange={e => setApprovalForm(p => ({ ...p, object_type: e.target.value }))} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Approver</label>
                    <input value={approvalForm.approver} onChange={e => setApprovalForm(p => ({ ...p, approver: e.target.value }))} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Required Role</label>
                    <input value={approvalForm.required_role} onChange={e => setApprovalForm(p => ({ ...p, required_role: e.target.value }))} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
                  <button onClick={handleApprovalCreate} disabled={approvalSaving} style={{ padding: '10px 20px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    {approvalSaving ? 'Requesting...' : 'Request Approval'}
                  </button>
                  <button onClick={() => setApprovalView('list')} style={{ padding: '10px 20px', background: 'none', color: '#6b7280', border: 'none', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Approvals Detail View */}
          {approvalView === 'detail' && selectedApproval && (
            <div style={{ maxWidth: 700 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <button onClick={() => setApprovalView('list')} style={{ background: 'none', border: 'none', fontSize: 13, color: '#4f46e5', cursor: 'pointer', fontWeight: 600 }}>← Back</button>
                <h3 style={{ fontSize: 16, margin: 0 }}>{selectedApproval.title}</h3>
                <div style={{ marginLeft: 'auto' }}>{renderBadge(selectedApproval.status || 'pending', APPROVAL_STATUS_COLORS)}</div>
              </div>

              <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 24 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', marginBottom: 20 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 2 }}>Case ID</div>
                    <div style={{ fontSize: 13, color: '#1e293b', fontFamily: 'monospace' }}>{selectedApproval.case_id || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 2 }}>Object Type</div>
                    <div style={{ fontSize: 13, color: '#1e293b' }}>{selectedApproval.object_type || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 2 }}>Requested By</div>
                    <div style={{ fontSize: 13, color: '#1e293b' }}>{selectedApproval.requested_by || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 2 }}>Approver</div>
                    <div style={{ fontSize: 13, color: '#1e293b' }}>{selectedApproval.approver || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 2 }}>Required Role</div>
                    <div style={{ fontSize: 13, color: '#1e293b' }}>{selectedApproval.required_role || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 2 }}>Created</div>
                    <div style={{ fontSize: 13, color: '#1e293b' }}>{selectedApproval.created_at ? new Date(selectedApproval.created_at).toLocaleString() : '—'}</div>
                  </div>
                </div>

                {selectedApproval.description && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>Description</div>
                    <div style={{ fontSize: 13, color: '#1e293b', whiteSpace: 'pre-wrap' }}>{selectedApproval.description}</div>
                  </div>
                )}

                {selectedApproval.rationale && (
                  <div style={{ marginBottom: 20, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>Decision Rationale</div>
                    <div style={{ fontSize: 13, color: '#1e293b', whiteSpace: 'pre-wrap' }}>{selectedApproval.rationale}</div>
                  </div>
                )}

                {/* Decision Actions (only for pending) */}
                {selectedApproval.status === 'pending' && (
                  <div style={{ paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 10 }}>Decision</div>
                    {showDecision ? (
                      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                          Rationale for "{showDecision.replace(/_/g, ' ')}":
                        </label>
                        <textarea
                          value={decisionRationale}
                          onChange={e => setDecisionRationale(e.target.value)}
                          rows={3}
                          placeholder="Provide rationale for your decision..."
                          style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, marginBottom: 10, fontFamily: 'inherit' }}
                        />
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={submitDecision} disabled={approvalSaving} style={{ padding: '8px 16px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                            {approvalSaving ? 'Submitting...' : 'Confirm'}
                          </button>
                          <button onClick={() => { setShowDecision(''); setDecisionRationale(''); }} style={{ padding: '8px 16px', background: 'none', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button onClick={() => handleDecision('approved')} style={{ padding: '8px 16px', background: '#059669', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          Approve
                        </button>
                        <button onClick={() => handleDecision('rejected')} style={{ padding: '8px 16px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          Reject
                        </button>
                        <button onClick={() => handleDecision('returned_for_revision')} style={{ padding: '8px 16px', background: '#d97706', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          Return for Revision
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
